import {DEMO_REQUEST_PATH} from '@/components/www/demo-request/demoPage'
import {LISTING_REJECTIONS_PATH} from '@/components/www/listing-rejections/listingRejectionsLp'
import type {LandingFaqItem} from '@/components/www/landing/landingFaq'
import type {LandingStat} from '@/components/www/landing/landingStats'
import type {
    NordstromAboutPillar,
    NordstromDesireCard,
    NordstromDoneItem,
    NordstromHowStep,
} from '@/components/www/sell-on-nordstrom/nordstromLp'

export const SELL_ON_MARKETPLACES_PATH = '/sell-on-marketplaces'

export function marketplacesDemoPath(cta: string): string {
    return `${DEMO_REQUEST_PATH}?source=sell-on-marketplaces&cta=${encodeURIComponent(cta)}`
}

export const MARKETPLACES_LP = {
    meta: {
        title: 'Get your fashion brand on Nordstrom, Macy\'s, Target+, and more',
        description:
            'Osello gets fashion brands accepted and live on Nordstrom, Macy\'s, Bloomingdale\'s, and Target+. Brand book, application, listings. You connect Shopify.',
    },
    hero: {
        lines: [
            'Your fashion brand.',
            'On every marketplace.',
            'We handle the rest.',
        ] as const,
        sub:
            'Nordstrom, Macy\'s, Bloomingdale\'s, or Target+. We build the brand book, submit the application, and list your catalog. You take one call and connect Shopify.',
        cta: 'See if your brand qualifies',
        ctaNote: 'A 15 minute call. No commitment, no pitch deck.',
        ctaSupport: 'Universal Standard and Furniq UK went live through Osello.',
        logosLabel: 'Where Osello puts brands',
    },
    statsBar: {
        items: [
            {id: 'furniq-time', value: '3 weeks', label: 'Furniq UK · live on Nordstrom'},
            {id: 'approval', value: '93%', label: 'Universal Standard · listings approved first pass'},
            {id: 'skus', value: '5,000+', label: 'Universal Standard · SKUs syncing daily'},
        ] satisfies LandingStat[],
    },
    desire: {
        statement: 'See your name on the marketplaces that matter.',
        support:
            'On Nordstrom, Macy\'s, Bloomingdale\'s, and Target+, your label sits next to the ones your customers already trust. Buyers, press, and every future retail partner read that placement.',
        browserUrl: 'nordstrom.com',
        cards: [
            {
                id: 'credibility',
                title: 'Credibility',
                body: '"Available at Nordstrom" and "Available at Macy\'s" do the selling for you. With customers, with press, with every retail conversation that follows.',
            },
            {
                id: 'customers',
                title: 'Full price',
                body: 'These shoppers come for quality, not markdowns. Your margins stay where you set them.',
            },
            {
                id: 'control',
                title: 'No wholesale risk',
                body: 'No purchase orders, no consignment, no net-60. You ship from your own inventory and keep control.',
            },
        ] satisfies NordstromDesireCard[],
    },
    doneForYou: {
        title: 'Worried about getting approved? We handle everything.',
        subhead:
            'Most brands apply cold, wait months, and never hear back. Getting accepted is a real project. Brand presentation, application, catalog specs, imagery requirements. It is our whole job, so it never becomes yours.',
        items: [
            {
                id: 'brand-book',
                num: '01',
                title: 'Your brand book',
                body: 'The presentation the marketplace team reviews. Story, positioning, target customer, visual identity. Built from your website, by us.',
            },
            {
                id: 'application',
                num: '02',
                title: 'Your application',
                body: 'Prepared, submitted, and managed through every round of back and forth until you are approved.',
            },
            {
                id: 'listings',
                num: '03',
                title: 'Your listings',
                body: 'Your Shopify catalog mapped to each marketplace\'s spec. Categories, size charts, imagery. Nothing gets rejected twice.',
            },
            {
                id: 'sync',
                num: '04',
                title: 'Your operations',
                body: 'Once you are live, inventory, prices, and orders flow between Shopify and the marketplace on their own.',
            },
        ] satisfies NordstromDoneItem[],
        closer: 'You approve. You ship. That\'s it.',
    },
    howItWorks: {
        title: 'Three steps to live.',
        steps: [
            {
                id: 'call',
                step: '01',
                title: 'Take the call',
                body: 'Fifteen minutes with our team. We will confirm if your brand might fit and tell you exactly what happens next.',
            },
            {
                id: 'connect',
                step: '02',
                title: 'Connect Shopify',
                body: 'One click. Products, variants, images, inventory. No exports, no spreadsheets.',
            },
            {
                id: 'apply',
                step: '03',
                title: 'Go live',
                body: 'We build the brand book, submit the application, set up the listings. Then your brand is on the marketplace.',
            },
        ] satisfies NordstromHowStep[],
        cta: 'Book my intro call',
    },
    about: {
        title: 'So, who is Osello?',
        lede:
            'Osello is the marketplace platform built for fashion brands. One connection to Shopify puts your catalog on the retailers that matter and keeps it in sync every day, with a team behind it that has done this before.',
        pillars: [
            {
                id: 'platform',
                title: 'Software does the heavy lifting',
                body:
                    'Products, imagery, inventory, and orders sync between Shopify and each marketplace daily. One brand runs 5,000+ SKUs through it without a single spreadsheet. Our team handles everything the software can\'t.',
            },
            {
                id: 'fashion',
                title: 'Fashion is the specialty',
                body:
                    'Size runs, colorways, seasonal drops, imagery standards. We speak retail fashion fluently, which is why brands like Universal Standard and Furniq UK trust us with their catalogs.',
            },
            {
                id: 'growth',
                title: 'Get in once, grow everywhere',
                body:
                    'The catalog we build for one retailer is ready for the next. Nordstrom, Macy\'s, Bloomingdale\'s, Target+. Same Shopify connection.',
            },
        ] satisfies NordstromAboutPillar[],
        cta: 'Meet us on the intro call',
    },
    stories: {
        eyebrow: 'Customer stories',
        title: 'Hear it from brands already on these marketplaces.',
        sub: 'Fashion founders on what it took to get approved, and what changed when Osello took over.',
    },
    faq: {
        title: 'Questions, answered.',
        items: [
            {
                id: 'wholesale',
                question: 'Is this the same as selling wholesale?',
                answer:
                    'No. This is marketplace retail. You sell under your own brand, at full price, from your own inventory. No purchase orders, no consignment, no waiting for a buyer to pick up your line.',
            },
            {
                id: 'cost',
                question: 'What does it cost?',
                answer:
                    'The intro call is free. If we take your brand on, we walk through pricing on that call. You will know every number before you commit to anything.',
            },
            {
                id: 'us-presence',
                question: 'We\'re not a US company. Can we still get on?',
                answer:
                    'Most of these marketplaces require a US entity or US fulfillment. If you ship from a US warehouse or 3PL, you can qualify. Furniq UK is a British brand and went live in three weeks.',
            },
            {
                id: 'orders',
                question: 'Who handles orders, shipping, and returns?',
                answer:
                    'You fulfill the way you already do. Marketplace orders land in your Shopify like any other order, and we keep inventory and order status in sync on both sides so nothing oversells.',
            },
            {
                id: 'timeline',
                question: 'How long until my brand is live?',
                answer:
                    'Every catalog is different, so we will not promise a date before we have seen yours. For reference, Furniq UK went live in three weeks, and Universal Standard\'s first listing was live five days after connecting.',
            },
            {
                id: 'rejection',
                question: 'What if the marketplace says no?',
                answer:
                    'We only submit brands we believe will be approved. That is what the intro call is for. If something needs fixing first, we tell you exactly what it is and how to get there.',
            },
            {
                id: 'photography',
                question: 'Will my photography pass?',
                answer:
                    'Your existing ecommerce imagery is the starting point. On the call we flag anything a marketplace requires that you do not have, before it ever becomes a rejection.',
            },
            {
                id: 'channels',
                question: 'Do I have to change how I sell today?',
                answer:
                    'No. A new marketplace is additive. Your Shopify store, your site, your other channels all keep running exactly as they do now.',
            },
            {
                id: 'existing-provider',
                question: 'We already sell on a marketplace. Can you take over from our current provider?',
                answer: 'Yes, and that is a different conversation from this one.',
                link: {label: 'Start here instead', to: LISTING_REJECTIONS_PATH},
            },
        ] satisfies LandingFaqItem[],
    },
    finalCta: {
        title: 'The next marketplace is one call away.',
        subhead: 'Fifteen minutes to see if your brand qualifies. We handle everything after that.',
        costNote: 'The call is free. You will know every number before you commit to anything.',
    },
} as const
